import { useFunction } from "@aec-craft/nb-generative";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Separator,
} from "@app/ui";
import {
	useClerk,
	useOrganization,
	useOrganizationList,
	useUser,
} from "@clerk/react";
import { apiClient } from "@fe/api";
import { useAsyncFunction } from "@fe/hooks/use-async-function";
import { useUnmounted } from "@fe/hooks/use-unmounted";
import { useTranslate } from "@fe/i18n/use-translate";
import { RouterPrivate } from "@fe/router-private";
import { isProspectInvitePath } from "@fe/utils/is-prospect-invite-path";
import { usePostHog } from "@posthog/react";
import { LogOutIcon, PlusIcon } from "lucide-react";
import {
	type PropsWithChildren,
	useEffect,
	useState,
	useSyncExternalStore,
} from "react";

const listPageSize = 50;
const noSetups: OnboardingSetup[] = [];

export const HasOrganization = ({ children }: PropsWithChildren) => {
	const { user } = useUser();
	const t = useTranslate();
	const { signOut, setActive } = useClerk();
	const { organization } = useOrganization();
	const posthog = usePostHog();
	const pathname = useSyncExternalStore(
		RouterPrivate.subscribe,
		() => RouterPrivate.state.location.pathname,
	);
	const isInvitationPage =
		pathname === "/invitation" || pathname.startsWith("/invitation/");
	const isOnboardingPage = pathname === "/onboarding";
	const isProspectInvitePage = isProspectInvitePath(pathname);

	const hasOrgMembership = !!user && user.organizationMemberships.length > 0;
	const [isChoosingOrganization, setIsChoosingOrganization] = useState(
		!hasOrgMembership,
	);

	if (!hasOrgMembership && !isChoosingOrganization) {
		setIsChoosingOrganization(true);
	}

	const showOrganizationList =
		!isInvitationPage &&
		!isOnboardingPage &&
		!isProspectInvitePage &&
		(!hasOrgMembership || (isChoosingOrganization && !organization));

	const handleSignOut = useFunction(() => {
		posthog?.reset();
		signOut();
	});

	useEffect(() => {
		if (organization || isChoosingOrganization || isInvitationPage) return;

		// If no active organisation set to the first one in org list
		if (user && user.organizationMemberships.length > 0) {
			const firstOrg = user.organizationMemberships[0].organization;
			setActive({ organization: firstOrg });
		}
	}, [organization, isChoosingOrganization, isInvitationPage, setActive, user]);

	return (
		<>
			{!showOrganizationList && children}
			{showOrganizationList && (
				<div className="absolute inset-0 z-1000 flex flex-col items-center justify-center gap-4 overflow-y-auto bg-background p-4">
					<OrganizationChooser />
					<Button variant="ghost" onClick={handleSignOut}>
						{t.userMenu.signOut}
						<LogOutIcon className="ml-2 size-4" />
					</Button>
				</div>
			)}
		</>
	);
};

function OrganizationChooser() {
	const t = useTranslate();
	const { user } = useUser();
	const {
		isLoaded,
		setActive,
		userMemberships,
		userInvitations,
		userSuggestions,
	} = useOrganizationList({
		userMemberships: { infinite: true, pageSize: listPageSize },
		userInvitations: { infinite: true, pageSize: listPageSize },
		userSuggestions: {
			infinite: true,
			pageSize: listPageSize,
			status: ["pending", "accepted"],
		},
	});
	const [busyId, setBusyId] = useState<string>();
	const [setups, setSetups] = useState<OnboardingSetup[]>(noSetups);
	const unmounted = useUnmounted();
	const memberships = userMemberships.data ?? [];
	const invitations = userInvitations.data ?? [];
	const suggestions = userSuggestions.data ?? [];

	useEffect(() => {
		fetchSetups()
			.catch(() => undefined)
			.then((value) => {
				if (!unmounted.current) setSetups(value ?? noSetups);
			});
	}, [unmounted]);

	const onResume = useAsyncFunction(async (setup: OnboardingSetup) => {
		if (!setup.orgId) {
			RouterPrivate.navigate(`/onboarding?setupId=${setup.id}`);
			return;
		}
		await setActive?.({ organization: setup.orgId });
		RouterPrivate.navigate("/org/billing");
	}, t.onboarding.createFailed);

	const onSelect = useFunction(async (organizationId: string) => {
		await setActive?.({ organization: organizationId });
		RouterPrivate.navigate("/project?page=1");
	});

	const onAccept = useAsyncFunction(
		async (invitation: (typeof invitations)[number]) => {
			setBusyId(invitation.id);
			try {
				await invitation.accept();
				await Promise.all([
					userInvitations.revalidate?.(),
					userMemberships.revalidate?.(),
					user?.reload(),
				]);
			} finally {
				setBusyId(undefined);
			}
		},
		t.onboarding.acceptFailed,
	);

	const onRequestToJoin = useAsyncFunction(
		async (suggestion: (typeof suggestions)[number]) => {
			setBusyId(suggestion.id);
			try {
				await suggestion.accept();
				await userSuggestions.revalidate?.();
			} finally {
				setBusyId(undefined);
			}
		},
		t.onboarding.joinFailed,
	);

	return (
		<Card className="w-full max-w-md">
			<CardHeader>
				<CardTitle>{t.onboarding.chooseOrg}</CardTitle>
				{isLoaded && memberships.length === 0 && (
					<CardDescription>{t.onboarding.noOrgs}</CardDescription>
				)}
			</CardHeader>
			<CardContent className="flex flex-col gap-4">
				{memberships.length > 0 && (
					<OrgListGroup title={t.onboarding.yourOrgs}>
						{memberships.map(({ id, organization }) => (
							<Button
								key={id}
								variant="outline"
								className="justify-start"
								onClick={() => onSelect(organization.id)}
							>
								{organization.name}
							</Button>
						))}
						{userMemberships.hasNextPage && (
							<Button variant="ghost" onClick={userMemberships.fetchNext}>
								{t.onboarding.showMore}
							</Button>
						)}
					</OrgListGroup>
				)}
				{setups.length > 0 && (
					<OrgListGroup title={t.onboarding.unfinishedSetups}>
						{setups.map((setup) => (
							<OrgListItem key={setup.id} name={setup.orgName}>
								<span className="shrink-0 text-muted-foreground">
									{!setup.orgId && t.onboarding.setupNotCreated}
									{setup.orgId &&
										setup.mode === "paid" &&
										t.onboarding.setupWaitsForPayment}
									{setup.orgId &&
										setup.mode === "trial" &&
										t.onboarding.setupWaitsForTrial}
								</span>
								<Button size="sm" onClick={() => onResume(setup)}>
									{t.onboarding.resumeSetup}
								</Button>
							</OrgListItem>
						))}
					</OrgListGroup>
				)}
				{invitations.length > 0 && (
					<OrgListGroup title={t.onboarding.invitations}>
						{invitations.map((invitation) => (
							<OrgListItem
								key={invitation.id}
								name={invitation.publicOrganizationData.name}
							>
								<Button
									size="sm"
									disabled={busyId !== undefined}
									onClick={() => onAccept(invitation)}
								>
									{t.onboarding.accept}
								</Button>
							</OrgListItem>
						))}
					</OrgListGroup>
				)}
				{suggestions.length > 0 && (
					<OrgListGroup title={t.onboarding.suggestions}>
						{suggestions.map((suggestion) => (
							<OrgListItem
								key={suggestion.id}
								name={suggestion.publicOrganizationData.name}
							>
								<Button
									size="sm"
									variant="outline"
									disabled={
										suggestion.status === "accepted" || busyId !== undefined
									}
									onClick={() => onRequestToJoin(suggestion)}
								>
									{suggestion.status === "accepted" && t.onboarding.requested}
									{suggestion.status !== "accepted" &&
										t.onboarding.requestToJoin}
								</Button>
							</OrgListItem>
						))}
					</OrgListGroup>
				)}
				{user?.createOrganizationEnabled && <Separator />}
				{user?.createOrganizationEnabled && (
					<Button onClick={() => RouterPrivate.navigate("/onboarding")}>
						<PlusIcon data-icon="inline-start" />
						{t.onboarding.createOrg}
					</Button>
				)}
			</CardContent>
		</Card>
	);
}

function OrgListGroup({
	title,
	children,
}: PropsWithChildren<{ title: string }>) {
	return (
		<div className="flex flex-col gap-2">
			<p className="text-sm font-medium text-muted-foreground">{title}</p>
			{children}
		</div>
	);
}

function OrgListItem({ name, children }: PropsWithChildren<{ name: string }>) {
	return (
		<div className="flex items-center justify-between gap-2 text-sm">
			<span className="min-w-0 truncate">{name}</span>
			{children}
		</div>
	);
}

async function fetchSetups() {
	const res = await apiClient.onboarding.setup.$get();
	return res.ok ? (await res.json()).setups : undefined;
}

type OnboardingSetup = NonNullable<
	Awaited<ReturnType<typeof fetchSetups>>
>[number];
